import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  MDSSessionContextValue,
  MDSSessionAction,
  MDSSessionArtifact,
} from "./mds-session-context.types";
import { mdsSessionReducer, createInitialState } from "./mds-session-context.reducer";
import { useEscapeKeyHandler, useAutoCollapseReasoningGroups } from "./mds-session-context.hooks";
import { useSessionItems } from "@/mds/components/sessions/session-item";
import {
  IMDSSessionProps,
  SessionEvent,
  SessionStatus,
} from "@/mds/components/sessions/session.types";
import { SessionViewMode } from "@/mds/components/sessions/session.constants";

const MDSSessionContext = createContext<MDSSessionContextValue | null>(null);

type MDSSessionProviderProps = Pick<
  IMDSSessionProps,
  "initialSessionEvents" | "initialSessionStatus" | "config" | "handlers"
> & {
  children: ReactNode;
};

export const MDSSessionProvider = forwardRef<MDSSessionContextValue, MDSSessionProviderProps>(
  (
    {
      children,
      initialSessionEvents,
      initialSessionStatus,
      config,
      handlers: { handleViewModeChanged, handleCountdownCompleted },
    },
    ref
  ) => {
    const [state, dispatch] = useReducer(
      mdsSessionReducer,
      createInitialState({
        sessionEvents: initialSessionEvents,
        sessionStatus: initialSessionStatus,
      })
    );

    const previousSessionStatusRef = useRef(initialSessionStatus);

    const actions = useMemo(
      () => ({
        viewArtifact: ({ payload }: { payload: MDSSessionArtifact }) => {
          dispatch({
            type: MDSSessionAction.SetActiveArtifact,
            payload,
          });
        },

        closeArtifact: () => {
          dispatch({
            type: MDSSessionAction.SetActiveArtifact,
            payload: null,
          });
        },

        addSessionEvent: (event: SessionEvent) => {
          dispatch({
            type: MDSSessionAction.AddSessionEvent,
            payload: event,
          });
        },

        updateSessionEvents: (events: SessionEvent[]) => {
          dispatch({
            type: MDSSessionAction.UpdateSessionEvents,
            payload: events,
          });
        },

        updateSessionStatus: (status: SessionStatus) => {
          dispatch({
            type: MDSSessionAction.UpdateSessionStatus,
            payload: status,
          });
        },

        minimizeSession: () => {
          dispatch({
            type: MDSSessionAction.MinimizeSession,
          });
        },

        expandSession: () => {
          dispatch({
            type: MDSSessionAction.ExpandSession,
          });
        },

        expandFromNotification: () => {
          dispatch({ type: MDSSessionAction.ExpandSession });
          dispatch({ type: MDSSessionAction.SetNotificationState, payload: false });
          dispatch({ type: MDSSessionAction.StopCountdown });
        },

        toggleReasoningGroup: (groupId: string) => {
          dispatch({ type: MDSSessionAction.ToggleReasoningGroup, payload: { groupId } });
        },

        autoCollapseReasoningGroup: (groupId: string) => {
          dispatch({ type: MDSSessionAction.AutoCollapseReasoningGroup, payload: { groupId } });
        },

        startCountdown: (durationMs: number) => {
          dispatch({ type: MDSSessionAction.StartCountdown, payload: { durationMs } });
        },

        stopCountdown: () => {
          dispatch({ type: MDSSessionAction.StopCountdown });
        },

        pauseCountdown: () => {
          dispatch({ type: MDSSessionAction.PauseCountdown });
        },

        resumeCountdown: () => {
          dispatch({ type: MDSSessionAction.ResumeCountdown });
        },

        setInputFocused: (focused: boolean) => {
          dispatch({ type: MDSSessionAction.SetInputFocused, payload: focused });
        },

        setHovering: (hovering: boolean) => {
          dispatch({ type: MDSSessionAction.SetHovering, payload: hovering });
        },

        setNotificationState: (value: boolean) => {
          dispatch({ type: MDSSessionAction.SetNotificationState, payload: value });
        },
      }),
      [dispatch]
    );

    const handleCountdownComplete = useCallback(() => {
      actions.stopCountdown();
      actions.setNotificationState(false);
      handleCountdownCompleted();
    }, [actions, handleCountdownCompleted]);

    // Simplified event handlers - just update state, effects handle pause/resume
    const handleHoverExtension = useCallback(() => {
      actions.setHovering(true);
    }, [actions]);

    const handleUnhoverExtension = useCallback(() => {
      actions.setHovering(false);
    }, [actions]);

    const handleInputFocus = useCallback(() => {
      actions.setInputFocused(true);
    }, [actions]);

    const handleInputBlur = useCallback(() => {
      actions.setInputFocused(false);
    }, [actions]);

    const handleInputChange = useCallback(() => {
      // For typing, we want to focus the input if not already focused
      if (!state.isInputFocused) {
        actions.setInputFocused(true);
      }
    }, [state.isInputFocused, actions]);

    // Effect to manage countdown pause/resume based on state
    useEffect(() => {
      if (!state.isCountdownActive) {
        return;
      }

      const shouldPause = state.isInputFocused || state.isHovering;

      if (shouldPause && !state.isCountdownPaused) {
        actions.pauseCountdown();
      } else if (!shouldPause && state.isCountdownPaused) {
        actions.resumeCountdown();
      }
    }, [
      state.isCountdownActive,
      state.isInputFocused,
      state.isHovering,
      state.isCountdownPaused,
      actions,
    ]);

    useEscapeKeyHandler(!!state.activeArtifact, actions.closeArtifact);

    const sessionItems = useSessionItems({
      sessionEvents: state.sessionEvents,
      config,
    });

    useAutoCollapseReasoningGroups({
      sessionItems,
      reasoningGroups: state.reasoningGroups,
      autoCollapseReasoningGroup: actions.autoCollapseReasoningGroup,
    });

    const sessionViewMode = useMemo<SessionViewMode>(() => {
      if (state.isInNotificationState) {
        return SessionViewMode.Notification;
      }

      if (state.isMinimized) {
        if (
          state.sessionStatus === SessionStatus.Waiting ||
          state.sessionStatus === SessionStatus.Processing
        ) {
          return SessionViewMode.CollapsedSpinner;
        }

        return SessionViewMode.Minimized;
      }

      return SessionViewMode.Standard;
    }, [state.isMinimized, state.sessionStatus, state.isInNotificationState]);

    useEffect(() => {
      handleViewModeChanged({ sessionViewMode });
    }, [handleViewModeChanged, sessionViewMode]);

    useEffect(() => {
      /**
       * If we flip from "Processing" to "Finished"...
       * ...and we are in a "collapsed" state...
       * ...then we should transition to "notification" state.
       */
      const previousStatus = previousSessionStatusRef.current;
      const currentStatus = state.sessionStatus;

      previousSessionStatusRef.current = currentStatus;

      if (
        sessionViewMode !== SessionViewMode.CollapsedSpinner &&
        sessionViewMode !== SessionViewMode.Minimized
      ) {
        return;
      }

      if (previousStatus !== SessionStatus.Finished && currentStatus === SessionStatus.Finished) {
        dispatch({ type: MDSSessionAction.SetNotificationState, payload: true });
        dispatch({ type: MDSSessionAction.StartCountdown, payload: { durationMs: 8000 } });
      }
    }, [state.sessionStatus, sessionViewMode]);

    const isExpanded = useMemo(() => {
      if (
        sessionViewMode === SessionViewMode.Standard ||
        sessionViewMode === SessionViewMode.Notification
      ) {
        return true;
      }

      return false;
    }, [sessionViewMode]);

    const isSessionTitleVisible = isExpanded;
    const isCountdownVisible = isExpanded && state.isCountdownActive;
    const isInputFormVisible = isExpanded && sessionViewMode !== SessionViewMode.Notification;
    const isSessionViewExpanded = isExpanded;

    const contextValue: MDSSessionContextValue = {
      sessionItems,
      sessionViewMode,
      sessionConfig: config,
      sessionEvents: state.sessionEvents,
      sessionStatus: state.sessionStatus,
      activeArtifact: state.activeArtifact,
      reasoningGroups: state.reasoningGroups,
      countdownDurationMs: state.countdownDurationMs,
      isCountdownPaused: state.isCountdownPaused,
      isSessionTitleVisible,
      isCountdownVisible,
      isInputFormVisible,
      isSessionViewExpanded,

      // Actions
      viewArtifact: actions.viewArtifact,
      closeArtifact: actions.closeArtifact,
      addSessionEvent: actions.addSessionEvent,
      updateSessionEvents: actions.updateSessionEvents,
      updateSessionStatus: actions.updateSessionStatus,
      minimizeSession: actions.minimizeSession,
      expandSession: actions.expandSession,
      expandFromNotification: actions.expandFromNotification,
      toggleReasoningGroup: actions.toggleReasoningGroup,
      autoCollapseReasoningGroup: actions.autoCollapseReasoningGroup,
      startCountdown: actions.startCountdown,
      stopCountdown: actions.stopCountdown,
      onCountdownComplete: handleCountdownComplete,
      handleHoverExtension,
      handleUnhoverExtension,
      handleInputFocus,
      handleInputBlur,
      handleInputChange,
    };

    useImperativeHandle(ref, () => contextValue);

    return <MDSSessionContext.Provider value={contextValue}>{children}</MDSSessionContext.Provider>;
  }
);

export const useMDSSession = (): MDSSessionContextValue => {
  const context = useContext(MDSSessionContext);

  if (!context) {
    throw new Error("useMDSSession must be used within a MDSSessionProvider");
  }

  return context;
};
